import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

import { AuthenticatedMobileShell } from '../components/AuthenticatedMobileShell.tsx';
import { CustomerForm } from '../components/CustomerForm.tsx';
import { StatusMessage } from '../components/StatusMessage.tsx';
import { WorkspaceContainer } from '../components/WorkspaceLayout.tsx';
import { useMe } from '../lib/auth.ts';
import { useCreateCounterparty } from '../lib/counterparties.ts';
import { CUSTOMER_SAVE_ERROR, type UpsertCounterpartyInput } from '../../shared/counterparty.ts';
import { CenteredMessage, CounterpartiesHeader, LoginScreen } from './counterparties.tsx';

export const Route = createFileRoute('/counterparties/new')({
  component: NewCounterpartyRoute,
});

export function NewCounterpartyRoute(): React.ReactElement {
  const me = useMe();

  if (me.isLoading) {
    return <CenteredMessage>Loading…</CenteredMessage>;
  }

  if (me.data == null) {
    return <LoginScreen />;
  }

  return (
    <AuthenticatedMobileShell currentPath="/counterparties/new">
      <AuthenticatedNewCounterparty />
    </AuthenticatedMobileShell>
  );
}

function AuthenticatedNewCounterparty(): React.ReactElement {
  const navigate = useNavigate();
  const createCounterparty = useCreateCounterparty();
  const [saveError, setSaveError] = useState<string | null>(null);

  async function save(input: UpsertCounterpartyInput): Promise<void> {
    setSaveError(null);
    try {
      const response = await createCounterparty.mutateAsync(input);
      await navigate({
        to: '/counterparties/$id',
        params: { id: response.counterparty.id },
        search: { saved: true },
      });
    } catch {
      setSaveError(CUSTOMER_SAVE_ERROR);
    }
  }

  return (
    <div className="min-h-screen">
      <WorkspaceContainer as="main" className="py-8">
        <div className="space-y-6">
          <CounterpartiesHeader title="New Customer" backLink={{ to: '/counterparties' }} />
          {saveError ? <StatusMessage tone="error">{saveError}</StatusMessage> : null}
          <CustomerForm
            submitLabel="Save"
            submitting={createCounterparty.isPending}
            onCancel={() => {
              void navigate({ to: '/counterparties' });
            }}
            onSubmit={save}
          />
        </div>
      </WorkspaceContainer>
    </div>
  );
}
